/**
 * Sending a workspace invite, shared by the Invite member dialog in Settings and the setup panel's
 * "Invite a team member" step (ACT2-1108).
 */
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';
import { type ExternalToast, toast } from 'sonner';

import { useOrpc } from '@/_shared/hooks/useOrpc';

export type InviteRole = 'member' | 'admin';

export function useInviteMember({
  onSent,
  toastOptions,
}: {
  onSent?: () => void;
  /** Where its toasts show, for a caller whose layout a default toast would cover. */
  toastOptions?: ExternalToast;
} = {}) {
  const orpc = useOrpc();
  const queryClient = useQueryClient();
  const mutation = useMutation(
    orpc.workspace.inviteMember.mutationOptions({
      onSuccess: () => {
        toast.success('Invitation sent', toastOptions);
        void queryClient.invalidateQueries({
          queryKey: orpc.workspace.listInvites.queryOptions({ input: { filter: 'active_pending' } }).queryKey,
        });
        // An invite completes a setup step (ACT2-1108).
        void queryClient.invalidateQueries({ queryKey: orpc.setup.status.queryOptions().queryKey });
        onSent?.();
      },
      onError: (err) => toast.error(err.message || 'Failed to send invitation', toastOptions),
    }),
  );

  const send = useCallback(
    (email: string, role: InviteRole) => {
      const trimmed = email.trim();
      if (!trimmed) {
        toast.error('Enter an email address', toastOptions);
        return;
      }
      mutation.mutate({ email: trimmed, role });
    },
    [mutation, toastOptions],
  );

  return { send, isPending: mutation.isPending };
}
